import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

/**
 * The editor's working copy of a page.
 *
 * Every change used to go straight to the database as it was typed — a
 * request per colour, a call per block added, another per reorder. There was
 * no draft and no way back: changing a colour to see how it looked changed
 * the stored page, and the only way to undo it was to remember the old value.
 * The "Save draft" button beside Publish set the page's *status* rather than
 * saving anything, and Cancel did not exist, because there was nothing to
 * cancel back to.
 *
 * So the page is held here while it is being worked on, and the whole thing
 * is sent when Save is pressed. Nothing reaches the server until then, which
 * is what makes Cancel a reload and nothing more.
 *
 * Sections and blocks created while editing get an id made here. The server
 * creates rows for them and sends back a map of which temporary id became
 * which real one, so the working copy can be relabelled without a reload —
 * otherwise the next save would create them all over again.
 */

let counter = 0;
/**
 * An id for something that does not exist yet.
 *
 * Deliberately not a 24-character hex string: that is how the server tells a
 * row it already has from one it has to create, so the two must not be
 * confusable.
 */
export const draftId = (what) => `draft-${what}-${Date.now().toString(36)}-${counter += 1}`;

export const isDraftId = (id) => !/^[a-f0-9]{24}$/i.test(String(id || ''));

export default function usePendingEdits({ brandId, pageId, page, setPage, reload }) {
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Read inside `save` without making it depend on every keystroke — a
  // callback that changed on each edit would restart the beforeunload
  // listener and anything else holding it.
  const latest = useRef(page);
  useEffect(() => { latest.current = page; }, [page]);

  /**
   * Change the working copy. Nothing is sent.
   *
   * The updater takes the current page and returns the next one, so a caller
   * cannot accidentally write a stale copy over a newer one.
   */
  const edit = useCallback((updater) => {
    setPage((prev) => (prev ? updater(prev) : prev));
    setDirty(true);
  }, [setPage]);

  /**
   * Send the working copy.
   *
   * `override` is for a caller that has just built the next page and cannot
   * wait for React to hand it back — switching some sections on and then
   * publishing in the same click, say. Reading state set a moment ago in the
   * same handler gives the version before the change.
   */
  const save = useCallback((override) => {
    const current = override || latest.current;
    if (!current) return Promise.resolve();
    setSaving(true);
    return API.saveSitePageTree({
      brand_id: brandId,
      page_id: pageId,
      // The array is the order, and what is absent from it is deleted. That
      // is the whole contract: this is how the page should look.
      sections: current.sectionData || [],
    })
      .then((res) => {
        if (res?.page) setPage(res.page);
        setDirty(false);
        toast.success('Draft saved');
        return res;
      })
      .catch((err) => {
        // Nothing was written, so the working copy is still the right thing
        // to show — losing an afternoon because a request timed out would be
        // far worse than leaving the page dirty.
        toast.error(err?.message || 'Could not save. Your changes are still here.');
        throw err;
      })
      .finally(() => setSaving(false));
  }, [brandId, pageId, setPage]);

  /** Throw the working copy away and show what the server has. */
  const discard = useCallback(() => {
    setDirty(false);
    return reload();
  }, [reload]);

  // The browser's own warning. It is the only thing that can stop a closed
  // tab taking unsaved work with it.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  return { dirty, saving, edit, save, discard, markClean: () => setDirty(false) };
}
